import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { addItemToPreviousSearch, searchDictionary } from "services/dictionary";

// DictionaryMode minus "save": saving a selection is a one-tap action and opens nothing.
export type DrawerMode = "define" | "translate";

// Owns the sidebar's search input, active tab, and the define/translate lookups.
export function useDictionarySearch() {
  const {
    mutate: runSearch,
    data: searchResult,
    isError,
    isPending,
    variables: lastSearch,
  } = useMutation({ mutationFn: searchDictionary });

  const [text, setText] = useState("");
  const [mode, setMode] = useState<DrawerMode>("define");

  const translate = useCallback((selection: string) => {
    setText(selection);
    setMode("translate");
  }, []);

  const define = useCallback(
    (selection: string) => {
      setText(selection);
      setMode("define");
      addItemToPreviousSearch(selection);
      runSearch({ text: selection });
    },
    [runSearch]
  );

  const applyLookup = useCallback(
    (lookupMode: DrawerMode, selection: string) => {
      if (lookupMode === "translate") {
        translate(selection);
      } else {
        define(selection);
      }
    },
    [translate, define]
  );

  const onSearch = (searchInput?: string) => {
    const searchText = searchInput || text;
    if (!searchText) {
      return;
    }
    define(searchText);
  };

  const onModeChanged = (newMode: DrawerMode) => {
    // Switching to Define auto-looks-up the box, but skip a word we already fetched
    if (newMode === "define" && text && text !== lastSearch?.text) {
      define(text);
    } else {
      setMode(newMode);
    }
  };

  return {
    text,
    setText,
    mode,
    onModeChanged,
    onSearch,
    applyLookup,
    result: searchResult ?? null,
    isError,
    isFetching: isPending,
  };
}
